/**
 * OzFDM: keep the header "More" dropdown open while the pointer is inside it.
 *
 * NOT a module, deliberately — see the note at the bottom of this comment.
 *
 * The dropdown lives in <overflow-list>'s shadow root, so moving the pointer
 * from the "More" button down into it crosses a shadow boundary: `pointerleave`
 * fires on the list item and `relatedTarget` is retargeted to the host element,
 * so header-menu.js's "am I moving into the overflow menu?" check misses. The
 * only thing left holding the menu open is the fallback guard in #deactivate():
 *
 *     if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;
 *
 * Both halves ask a shadow part whether it matches :hover. Chromium answers
 * yes; Firefox does not, so there the menu closes the instant the pointer
 * leaves the button and its links can never be reached.
 *
 * This replaces the first half with a geometry test — where is the pointer, and
 * is that inside the menu? Same question, answered from coordinates rather than
 * from :hover state across a shadow boundary, so every engine agrees.
 *
 * Nothing is moved, cloned or re-rendered: <overflow-list> keeps ownership of
 * its children and its reflow logic is untouched. If a future Horizon release
 * renames `overflowListHovered` the override is skipped and behaviour reverts
 * to stock — it cannot break the menu.
 *
 * Why a classic script and not `type="module"`: an import map is only honoured
 * if it appears before the first module script load is triggered. Loading this
 * as a module ahead of the theme's import map in snippets/scripts.liquid
 * invalidated that map, so every `@theme/*` specifier failed to resolve, no
 * component upgraded, and component.js logged "element not yet upgraded" while
 * dropping the pointer events — no dropdowns at all. A classic deferred script
 * never triggers module loading, so it cannot affect the map wherever it sits.
 */

(function () {
  let pointerX = -1;
  let pointerY = -1;
  let pointerKnown = false;
  let checkScheduled = false;

  /**
   * Is the pointer over some part of this menu — the "More" button, the
   * dropdown, one of its rows, or a flyout?
   *
   * `elementFromPoint` reports the host element for anything inside a shadow
   * root, so one containment check against <overflow-list> covers the panel and
   * its parts as well as the light-DOM items slotted into it.
   *
   * @param {Element | null | undefined} list - The <overflow-list> to test against.
   * @returns {boolean}
   */
  function isPointerInside(list) {
    if (!list || !pointerKnown) return false;

    const element = document.elementFromPoint(pointerX, pointerY);
    return Boolean(element) && (element === list || list.contains(/** @type {Node} */ (element)));
  }

  /**
   * Close any open dropdown the pointer is no longer inside.
   *
   * The override below can only ever keep a menu open, so on its own a missed
   * `pointerleave` would leave one stuck. Dispatching `pointerleave` at the
   * "More" item runs the theme's own close path — which consults the geometry
   * test and agrees the pointer has left — rather than reaching into private
   * state.
   */
  function closeIfPointerLeft() {
    for (const list of document.querySelectorAll('#header-group overflow-list')) {
      const isOpen = list.querySelector('[slot="more"] [aria-expanded="true"], [slot="overflow"] [aria-expanded="true"]');
      if (!isOpen || isPointerInside(list)) continue;

      const moreItem = list.querySelector('[slot="more"]');
      if (moreItem) {
        moreItem.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false, relatedTarget: document.body }));
      }
    }
  }

  /** Batched to one hit test per frame — `elementFromPoint` forces layout. */
  function scheduleCheck() {
    if (checkScheduled) return;

    checkScheduled = true;
    requestAnimationFrame(function () {
      checkScheduled = false;
      closeIfPointerLeft();
    });
  }

  document.addEventListener(
    'pointermove',
    function (event) {
      pointerX = event.clientX;
      pointerY = event.clientY;
      pointerKnown = true;
      scheduleCheck();
    },
    { capture: true, passive: true }
  );

  /* The pointer can leave the window without a final move inside it; forget
     where it was so a stale position cannot hold a menu open. */
  document.documentElement.addEventListener('pointerleave', function () {
    pointerKnown = false;
    closeIfPointerLeft();
  });

  customElements.whenDefined('header-menu').then(function () {
    const HeaderMenu = customElements.get('header-menu');
    if (!HeaderMenu) return;

    const existing = Object.getOwnPropertyDescriptor(HeaderMenu.prototype, 'overflowListHovered');
    if (!existing || typeof existing.get !== 'function') return; // renamed upstream — leave stock behaviour alone

    Object.defineProperty(HeaderMenu.prototype, 'overflowListHovered', {
      configurable: true,
      get: function () {
        return isPointerInside(this.refs && this.refs.overflowMenu);
      },
    });
  });
})();
/**
 * OzFDM perf: kick the header hydration early on touch devices.
 *
 * The first paint carries a light mobile drawer (top-level links only, see
 * snippets/ozfdm-header-drawer.liquid) and no sold-out pills; the hydration
 * pass in sections/header.liquid fills both in, and it starts on the first
 * pointerover / focusin / touchstart on #header-group or after 10 s. On a phone
 * the drawer is the whole navigation, so waiting for a touch on the header
 * itself means the first tap on the menu icon opens the light drawer and the
 * full one pops in a moment later. Start instead on the first touch or scroll
 * anywhere, or after 4 s, by dispatching the same event the module listens for.
 * Every path is guarded on the hydration side, so extra kicks are no-ops.
 */
(function () {
  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;

  var kicked = false;

  function kick() {
    var group = document.getElementById('header-group');
    if (!group) return;
    var section = group.querySelector('.shopify-section');
    if (section && section.dataset.hydrated === 'true') return;
    group.dispatchEvent(new Event('touchstart'));
  }

  function onIntent() {
    if (kicked) return;
    kicked = true;
    window.removeEventListener('touchstart', onIntent);
    window.removeEventListener('scroll', onIntent);
    kick();
  }

  window.addEventListener('touchstart', onIntent, { passive: true });
  window.addEventListener('scroll', onIntent, { passive: true });
  setTimeout(kick, 4000);
})();
